import React, {
  useRef, useState, useCallback, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@cbrebuild/blocks';

// delay before hiding lets the pointer cross the gap to the tooltip without it closing
const HIDE_DELAY_MS = 250;

const Tooltip = ({
  children,
  position,
  size,
  message,
  ...other
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const hideTimeoutRef = useRef(null);
  const contentRef = useRef(null);

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  // drop any leftover text selection so a reopened tooltip starts clean
  const clearContentSelection = () => {
    const selection = window.getSelection && window.getSelection();
    if (selection && contentRef.current && contentRef.current.contains(selection.anchorNode)) {
      selection.removeAllRanges();
    }
  };

  const handleMouseEnter = useCallback(() => {
    clearHideTimeout();
    setIsVisible(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      clearContentSelection();
    }, HIDE_DELAY_MS);
  }, []);

  useEffect(() => clearHideTimeout, []);

  const tooltipTextClassName = ['tooltip-text', position, size, isVisible && 'visible']
    .filter(Boolean)
    .join(' ');

  const tooltipText = (
    <div className="tooltip-text-wrapper">
      <div className={tooltipTextClassName}>
        <div className="tooltip-text-content" ref={contentRef}>
          {message}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="nd-tooltip"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      {...other}
    >
      {position === 'top' && tooltipText}
      <div className="target">{children}</div>
      {position === 'bottom' && tooltipText}
      {position === 'right' && tooltipText}
    </div>
  );
};

Tooltip.propTypes = {
  message: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]).isRequired,
  children: PropTypes.node,
  size: PropTypes.oneOf([
    'small',
    'medium',
  ]),
  position: PropTypes.oneOf([
    'top',
    'bottom',
    'right',
  ]),
};

Tooltip.defaultProps = {
  children: <Icon iconName="help" />,
  position: 'top',
  size: 'medium',
};

export default Tooltip;
